import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { tourService } from '../services/tourService';
import { scrollTourTarget, tourPopoverStyle } from '../utils/tourPosition';

export const TourOverlay: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState<any[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [onCompleteFn, setOnCompleteFn] = useState<(() => void) | null>(null);
  const [isPassive, setIsPassive] = useState(false);

  // Constants for dimensions
  const POP_WIDTH = 320;

  useEffect(() => {
    const handleStartTour = (e: any) => {
      const { steps: newSteps, onComplete } = e.detail;
      setSteps(newSteps);
      setOnCompleteFn(() => onComplete);
      setCurrentStepIndex(0);
      setIsPassive(tourService.isPassive());
      setIsActive(true);
    };

    const handleStopTour = () => {
      setIsActive(false);
      setSteps([]);
      setCurrentStepIndex(0);
    };

    const handleNextEvent = () => {
       setCurrentStepIndex(i => i + 1);
    };

    window.addEventListener('libritui-native-tour', handleStartTour);
    window.addEventListener('libritui-tour-stopped', handleStopTour);
    window.addEventListener('libritui-tour-next', handleNextEvent);

    return () => {
      window.removeEventListener('libritui-native-tour', handleStartTour);
      window.removeEventListener('libritui-tour-stopped', handleStopTour);
      window.removeEventListener('libritui-tour-next', handleNextEvent);
    };
  }, []);

  const currentStep = steps[currentStepIndex];

  // Tracker for target element position
  useEffect(() => {
    if (!isActive || !currentStep) return;

    let rafId: number;

    const updateRect = () => {
      if (currentStep.element) {
        const el = document.querySelector(currentStep.element);
        if (el) {
          const rect = el.getBoundingClientRect();
          // Check if it's completely out of view, might need scrolling
          // but for now just update rect
          setTargetRect(rect);
          
          if (currentStep.onHighlighted) {
              currentStep.onHighlighted(el);
          }
        } else {
          setTargetRect(null); // not found
        }
      } else {
        setTargetRect(null); // centered
      }
      rafId = requestAnimationFrame(updateRect);
    };

    // Scroll to element initially
    if (currentStep.element) {
      const el = document.querySelector(currentStep.element);
      if (el) {
         scrollTourTarget(el);
      }
    }

    rafId = requestAnimationFrame(updateRect);

    return () => cancelAnimationFrame(rafId);
  }, [isActive, currentStep]);

  if (!isActive || !currentStep) return null;

  const { popover } = currentStep;
  const isLastStep = currentStepIndex === steps.length - 1;

  const handleNext = () => {
    if (popover.onNextClick) {
       popover.onNextClick();
    }
    
    if (isLastStep) {
      setIsActive(false);
      tourService.stopTour();
      if (onCompleteFn) onCompleteFn();
    } else {
      setCurrentStepIndex(i => i + 1);
    }
  };

  const handleClose = () => {
     if (isPassive) {
        setIsActive(false);
        tourService.stopTour();
     }
  };

  // Calculate popover position
  // Balão sempre dentro da tela (abaixo, acima ou preso ao rodapé)
  const popoverStyle: React.CSSProperties = {
     ...tourPopoverStyle(targetRect, Math.min(POP_WIDTH, window.innerWidth - 32)),
     zIndex: 10001,
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] pointer-events-none">
         {/* Backdrop with cutout */}
         {targetRect ? (
            <motion.div
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                 position: 'absolute',
                 top: targetRect.top - 8,
                 left: targetRect.left - 8,
                 width: targetRect.width + 16,
                 height: targetRect.height + 16,
                 boxShadow: '0 0 0 9999px rgba(0,0,0,0.85)',
                 borderRadius: '12px',
                 pointerEvents: 'none',
                 transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease'
              }}
            />
         ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/85 pointer-events-none"
            />
         )}

         {/* Backdrop to block clicks if needed or allow close */}
         {targetRect ? (
            <>
               {/* Top */}
               <div className="absolute top-0 left-0 right-0 pointer-events-auto" style={{ height: targetRect.top - 8, zIndex: 10000 }} onClick={handleClose} />
               {/* Bottom */}
               <div className="absolute left-0 right-0 bottom-0 pointer-events-auto" style={{ top: targetRect.bottom + 8, zIndex: 10000 }} onClick={handleClose} />
               {/* Left */}
               <div className="absolute pointer-events-auto" style={{ top: targetRect.top - 8, left: 0, width: targetRect.left - 8, height: targetRect.height + 16, zIndex: 10000 }} onClick={handleClose} />
               {/* Right */}
               <div className="absolute pointer-events-auto" style={{ top: targetRect.top - 8, left: targetRect.right + 8, right: 0, height: targetRect.height + 16, zIndex: 10000 }} onClick={handleClose} />
            </>
         ) : (
            <div className="absolute inset-0 pointer-events-auto" style={{ zIndex: 10000 }} onClick={handleClose} />
         )}

         {/* Popover */}
         <motion.div
            layout
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            style={popoverStyle}
            className="bg-surface rounded-3xl p-6 pointer-events-auto flex flex-col gap-4 border border-black/[0.06] dark:border-white/[0.06]"
         >
             <div>
                <h3 className="font-bold text-lg text-ink mb-2 leading-tight">
                   {popover.title}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                   {popover.description}
                </p>
             </div>

             <div className="flex items-center justify-between mt-2 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
                <div className="flex gap-1">
                   {steps.map((_, i) => (
                      <div 
                         key={i} 
                         className={`h-1.5 rounded-full transition-all ${i === currentStepIndex ? 'w-4 bg-ink' : 'w-1.5 bg-slate-200 dark:bg-slate-700'}`}
                      />
                   ))}
                </div>
                
                {(!popover.showButtons || popover.showButtons.includes('next')) && (
                   <button
                     onClick={handleNext}
                     className="bg-primary text-slate-900 font-bold py-2 px-6 rounded-2xl hover:opacity-90 active:scale-95 transition-all text-sm"
                   >
                     {isLastStep ? (popover.doneBtnText || 'Finalizar') : (popover.nextBtnText || 'Próximo')}
                   </button>
                )}
             </div>
         </motion.div>
      </div>
    </AnimatePresence>
  );
};
